import { useEffect } from "react";

type Event = MouseEvent | TouchEvent;

/**
 * Adds event listeners for clicking/touching outside of a particular element.
 * Primarily intended to allow popovers to close on clicking/touching outside.
 * @param ref A react RefObject to the element in question (ie the popover we
 *            want to close when we click outside of it)
 * @param handler The event handler callback.
 */
export function useOnClickOutside<T extends HTMLElement = HTMLElement>(
  ref: React.RefObject<T>,
  handler: (event: Event) => void
) {
  useEffect(() => {
    const listener = (event: Event) => {
      if (!ref.current || ref.current.contains(event.target as Node)) return;
      handler(event);
    };

    document.addEventListener("click", listener);
    document.addEventListener("touchend", listener);

    return () => {
      document.removeEventListener("click", listener);
      document.removeEventListener("touchend", listener);
    };
  }, [ref, handler]);
}
